import { appVersion, defaultStartingElo } from "constants/gameDefaults";
import { Timestamp } from "@firebase/firestore";
import { User } from "./user";

export enum GameStateEnum {
  WAITING = "waiting",
  LIVE = "live",
  DRAFT = "draft",
  FINISHED = "finished",
  ROLE_SELECTION = "role selection",
  ABORTED = "aborted",
  CANCELLED = "cancelled",
}

export enum GameStatusEnum {
  CONNECTED = "connected",
  DISCONNECTED = "disconnected",
  RESULTS = "results",
  RESULTS_ERROR = "results error",
  MISSED_TIMER = "missed timer",
  SURREND = "surrend",
}

export enum GameModeEnum {
  FRIENDLY = "friendly",
  NORMAL = "normal",
  COMPETITIVE = "competitive",
  SIMULATION = "simulation",
}

export const isCompetitiveMode = (mode: GameModeEnum): boolean =>
  mode === GameModeEnum.COMPETITIVE;

export enum SideEnum {
  BLUE = "BLUE",
  RED = "RED",
}

export type UserChampion = {
  championId: string;
  userId: string;
};

type Role = "top" | "jungle" | "mid" | "bottom" | "support";
type ChampionId = string;

export type UserTeam = {
  team: Record<Role, ChampionId>;
  userId: string;
};

export type UserResult = {
  userId: string;
  score: string;
};

export interface Game {
  id: string | null;
  mode: GameModeEnum;
  players: string[];
  player1Elo?: number | null;
  player2Elo?: number | null;
  state: GameStateEnum;
  status: GameStatusEnum;
  currentTurn: number;
  bluePickedChampions: UserChampion[];
  redPickedChampions: UserChampion[];
  blueBannedChampions: UserChampion[];
  redBannedChampions: UserChampion[];
  rolesLockedIn: string[];
  teams: UserTeam[];
  winner: UserResult | null;
  loser: UserResult | null;
  date: string;
  version: string;
  lobbyId: string | null;
  timestamp: Timestamp | null;
}

export const getNewGame = (user: User | null, mode: GameModeEnum): Game => {
  if (!user) {
    throw new Error("User is null");
  }

  return {
    id: null,
    mode,
    players: [user.uid],
    player1Elo: user.currentElo ?? defaultStartingElo,
    state: GameStateEnum.WAITING,
    status: GameStatusEnum.CONNECTED,
    currentTurn: 0,
    bluePickedChampions: [],
    redPickedChampions: [],
    blueBannedChampions: [],
    redBannedChampions: [],
    rolesLockedIn: [],
    teams: [],
    winner: null,
    loser: null,
    date: new Date().toISOString(),
    version: `${appVersion}`,
    lobbyId: null,
    timestamp: null,
  };
};

export const getSimulationGame = (): Game => ({
  id: null,
  mode: GameModeEnum.SIMULATION,
  players: ["player1", "player2"],
  state: GameStateEnum.LIVE,
  status: GameStatusEnum.CONNECTED,
  currentTurn: 0,
  bluePickedChampions: [],
  redPickedChampions: [],
  blueBannedChampions: [],
  redBannedChampions: [],
  rolesLockedIn: [],
  teams: [],
  winner: null,
  loser: null,
  date: new Date().toISOString(),
  version: `${appVersion}`,
  lobbyId: null,
  timestamp: null,
});
